import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTrophy, 
  faUsers, 
  faHandshake, 
  faLeaf 
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import CallToAction from '../components/CallToAction';

const ChiSiamo: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="pt-28 md:pt-32 pb-20 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Chi Siamo</h1>
            <p className="text-xl text-white/80">
              Da oltre 10 anni aiutiamo famiglie e imprese a trovare le migliori soluzioni
              per risparmiare su luce, gas e internet.
            </p>
          </motion.div>
        </div>
      </section>

      {/* La Nostra Storia */}
      <section className="py-20">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:w-1/2 mb-12 lg:mb-0 lg:pr-12"
            >
              <h2 className="text-3xl font-bold mb-6 text-primary">La Nostra Storia</h2>
              <p className="mb-4">
                Servizio Clienti Energia nasce nel 2013 con l'obiettivo di semplificare il mercato dell'energia,
                rendendo più facile per i consumatori trovare le offerte più adatte alle loro esigenze.
              </p>
              <p className="mb-4">
                Nel corso degli anni abbiamo ampliato la nostra offerta, includendo anche soluzioni per la
                connettività internet, mantenendo sempre al centro la soddisfazione del cliente e la qualità del servizio.
              </p>
              <p>
                Oggi siamo un punto di riferimento nel settore, con migliaia di clienti soddisfatti
                che hanno scelto di affidarsi a noi per le loro forniture energetiche e di connettività.
              </p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:w-1/2"
            >
              <div className="bg-light-surface p-8 rounded-lg shadow-md">
                <div className="grid grid-cols-2 gap-6">
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center p-4"
                  >
                    <div className="text-4xl font-bold text-primary mb-2">10+</div>
                    <p className="text-light-muted">Anni di esperienza</p>
                  </motion.div>
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="text-center p-4"
                  >
                    <div className="text-4xl font-bold text-primary mb-2">15k+</div>
                    <p className="text-light-muted">Clienti soddisfatti</p>
                  </motion.div>
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="text-center p-4"
                  >
                    <div className="text-4xl font-bold text-primary mb-2">50+</div>
                    <p className="text-light-muted">Fornitori partner</p>
                  </motion.div>
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="text-center p-4"
                  >
                    <div className="text-4xl font-bold text-primary mb-2">30+</div>
                    <p className="text-light-muted">Consulenti esperti</p>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* I Nostri Valori */}
      <section className="py-20">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary">I Nostri Valori</h2>
            <p className="text-lg text-light-muted max-w-3xl mx-auto">
              Principi che guidano ogni nostra azione e decisione
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="card text-center"
            >
              <div className="inline-block p-4 rounded-full bg-primary/10 text-primary mb-6">
                <FontAwesomeIcon icon={faTrophy} className="text-3xl" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-light-text">Eccellenza</h3>
              <p className="text-light-muted">
                Puntiamo sempre al massimo della qualità in ogni servizio che offriamo.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="card text-center"
            >
              <div className="inline-block p-4 rounded-full bg-primary/10 text-primary mb-6">
                <FontAwesomeIcon icon={faUsers} className="text-3xl" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-light-text">Cliente al Centro</h3>
              <p className="text-light-muted">
                Ascoltiamo attentamente le esigenze dei nostri clienti per offrire soluzioni su misura.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="card text-center"
            >
              <div className="inline-block p-4 rounded-full bg-primary/10 text-primary mb-6">
                <FontAwesomeIcon icon={faHandshake} className="text-3xl" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-light-text">Trasparenza</h3>
              <p className="text-light-muted">
                Comunichiamo in modo chiaro e sincero, senza costi nascosti o sorprese.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="card text-center"
            >
              <div className="inline-block p-4 rounded-full bg-primary/10 text-primary mb-6">
                <FontAwesomeIcon icon={faLeaf} className="text-3xl" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-light-text">Sostenibilità</h3>
              <p className="text-light-muted">
                Promuoviamo soluzioni energetiche sostenibili per un futuro migliore.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="py-12 px-4"
      >
        <div className="container mx-auto">
          <CallToAction 
            title="Vuoi saperne di più?" 
            subtitle="Contatta il nostro team per scoprire come possiamo aiutarti a risparmiare"
            variant="accent"
            className="mt-8"
          />
        </div>
      </motion.section>
    </div>
  );
};

export default ChiSiamo;
